/*
 * ThemePicker — popover behavior.
 *
 * 1:1 port of metainventory's
 *   apps/status/src/components/ThemePicker/ThemePicker.tsx
 * for vanilla JS. Theme metadata is read off each row's data
 * attributes + the hero's swatch element copies row swatches when a
 * row becomes active. Theme persistence runs through window.setTheme
 * (defined in app.js).
 */
(function () {
    'use strict';

    function initPicker(root) {
        const trigger      = root.querySelector('[data-tp-trigger]');
        const popover      = root.querySelector('[data-tp-popover]');
        if (!trigger || !popover) return;

        const triggerName  = root.querySelector('[data-tp-trigger-name]');
        const triggerSwSb  = root.querySelector('[data-tp-trigger-sidebar]');
        const triggerSwAc  = root.querySelector('[data-tp-trigger-accent]');
        const triggerSw    = root.querySelector('[data-tp-trigger-swatch]');
        const heroTitle    = root.querySelector('[data-tp-hero-title]');
        const heroMeta     = root.querySelector('[data-tp-hero-meta]');
        const heroSwSb     = root.querySelector('[data-tp-hero-sidebar]');
        const heroSwAc     = root.querySelector('[data-tp-hero-accent]');
        const heroSwParent = root.querySelector('[data-tp-hero-swatch] .theme-picker-swatch');
        const list         = root.querySelector('[data-tp-list]');
        const rows         = root.querySelectorAll('.theme-picker-row');

        // Re-query each call: Lucide hydration replaces the <i> with an
        // <svg>, but data-* attributes are preserved on the new node.
        function getTriggerIcon() {
            return root.querySelector('[data-tp-trigger-icon]');
        }

        // Build {id → metadata} from server-rendered swatch markup +
        // row text. Avoids inlining the registry as JSON twice.
        const meta = {};
        rows.forEach((r) => {
            const id = r.dataset.tpId;
            const swatch = r.querySelector('.theme-picker-swatch');
            const sidebar = r.querySelector('.theme-picker-swatch-sidebar');
            const accent  = r.querySelector('.theme-picker-swatch-accent');
            const nameEl  = r.querySelector('.theme-picker-row-name');
            const descEl  = r.querySelector('.theme-picker-row-desc');
            // Lucide icon name (kebab-case), read from data-tp-icon on
            // the name container — set server-side from the theme registry.
            const iconName = nameEl ? (nameEl.dataset.tpIcon || 'circle') : 'circle';
            meta[id] = {
                id,
                label:        nameEl ? nameEl.textContent.trim() : id,
                shortLabel:   r.dataset.tpShortLabel || (nameEl ? nameEl.textContent.trim() : id),
                description:  descEl ? descEl.textContent.trim() : '',
                iconName,
                bgBase:       swatch  ? swatch.style.background  : '',
                bgSurface:    sidebar ? sidebar.style.background : '',
                sidebarBorder: sidebar ? sidebar.style.borderRightColor : '',
                accent:       accent  ? accent.style.background  : '',
            };
        });
        // Special-case 'system' — no inline swatch.
        meta['system'] = {
            id: 'system',
            label: 'System',
            description: 'Match operating system preference.',
            iconName: 'monitor',
            bgBase: '', bgSurface: '', sidebarBorder: '', accent: '',
        };

        function currentPreference() {
            return localStorage.getItem('theme') || 'system';
        }

        function resolvedTheme() {
            return document.documentElement.getAttribute('data-theme') || 'precision';
        }

        function setHeroFromMeta(m, isSystem) {
            if (heroTitle) heroTitle.textContent = m ? m.label : 'Custom';
            if (heroMeta)  heroMeta.textContent  = isSystem ? 'Following system' : (m ? m.description : '');
            if (m && heroSwSb && heroSwAc && heroSwParent) {
                heroSwParent.style.background = m.bgBase;
                heroSwSb.style.background = m.bgSurface;
                heroSwSb.style.borderRightColor = m.sidebarBorder;
                heroSwAc.style.background = m.accent;
            }
        }

        function setTriggerFromMeta(m) {
            if (!m) return;
            if (triggerName) triggerName.textContent = m.shortLabel || m.label || 'Theme';
            const existing = getTriggerIcon();
            if (existing) {
                // Skip re-hydration if the displayed icon already matches —
                // either the post-hydration class `.lucide-<name>` or the
                // pre-hydration `data-lucide` attribute. Note: on an SVG,
                // `element.className` is an SVGAnimatedString — read the
                // attribute as a plain string to keep `.match()` safe.
                const cls = existing.getAttribute('class') || '';
                const currentName = existing.getAttribute('data-lucide')
                    || (cls.match(/lucide-([\w-]+)/) || [])[1];
                if (currentName !== m.iconName) {
                    const fresh = document.createElement('i');
                    fresh.setAttribute('data-lucide', m.iconName);
                    fresh.setAttribute('data-tp-trigger-icon', '');
                    fresh.setAttribute('aria-hidden', 'true');
                    fresh.className = 'inline-block h-4 w-4';
                    existing.replaceWith(fresh);
                    if (window.Lucide) window.Lucide.createIcons({ icons: window.Lucide.icons });
                }
            }
            if (triggerSw && triggerSwSb && triggerSwAc) {
                triggerSw.style.background = m.bgBase;
                triggerSwSb.style.background = m.bgSurface;
                triggerSwSb.style.borderRightColor = m.sidebarBorder;
                triggerSwAc.style.background = m.accent;
            }
        }

        function syncActiveStates() {
            const pref = currentPreference();
            const resolved = resolvedTheme();
            rows.forEach((r) => {
                const id = r.dataset.tpId;
                const active = (id === pref) || (pref === 'system' && id === 'system');
                r.classList.toggle('is-active', active);
                r.setAttribute('aria-selected', active ? 'true' : 'false');
            });
            // Hero + trigger: when on 'system', show the resolved theme's swatch
            // but use the system's label/description for the hero.
            const triggerMeta = meta[resolved] || meta[pref];
            setTriggerFromMeta(triggerMeta);

            const heroMetaSrc = pref === 'system' ? meta[resolved] : meta[pref];
            setHeroFromMeta(heroMetaSrc, pref === 'system');
        }

        /* ─── Open / close ─────────────────────────────────── */

        let open = false;
        let cursor = 0;

        function setOpen(next) {
            open = next;
            popover.hidden = !open;
            trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
            if (open) {
                // Cursor starts on currently-selected row
                const pref = currentPreference();
                const visible = Array.from(rows);
                const idx = visible.findIndex((r) => r.dataset.tpId === pref);
                cursor = Math.max(idx, 0);
                applyCursor();
                popover.focus();
            }
        }

        function applyCursor() {
            rows.forEach((r, i) => r.classList.toggle('is-cursor', i === cursor));
            const node = rows[cursor];
            if (node && typeof node.scrollIntoView === 'function') {
                node.scrollIntoView({ block: 'nearest' });
            }
        }

        trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            setOpen(!open);
        });

        document.addEventListener('mousedown', (e) => {
            if (!open) return;
            if (!root.contains(e.target)) setOpen(false);
        });

        document.addEventListener('keydown', (e) => {
            if (!open) return;
            if (e.key === 'Escape') {
                setOpen(false);
                trigger.focus();
            }
        });

        popover.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                cursor = Math.min(cursor + 1, rows.length - 1);
                applyCursor();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                cursor = Math.max(cursor - 1, 0);
                applyCursor();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const r = rows[cursor];
                if (r) selectRow(r);
            }
        });

        rows.forEach((r, i) => {
            r.addEventListener('mouseenter', () => {
                cursor = i;
                applyCursor();
            });
            r.addEventListener('click', () => selectRow(r));
        });

        function selectRow(r) {
            const id = r.dataset.tpId;
            if (typeof window.setTheme === 'function') {
                window.setTheme(id);
            } else {
                localStorage.setItem('theme', id);
                document.documentElement.setAttribute('data-theme', id);
            }
            setOpen(false);
            syncActiveStates();
        }

        window.addEventListener('themechange', syncActiveStates);
        syncActiveStates();
    }

    function init() {
        document.querySelectorAll('[data-theme-picker]').forEach(initPicker);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
